import React from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import { normalizeGameType } from '../../utils/normalizeGameType';
import CompetitivePlayerDisplay from './CompetitivePlayerDisplay';
import CollaborativePlayerDisplay from './CollaborativePlayerDisplay';
import BreakTheCodePlayerDisplay from './BreakTheCodePlayerDisplay';
import AssociationsPlayerDisplay from './AssociationsPlayerDisplay';
import CatchShoutCatchPlayerDisplay from './CatchShoutCatchPlayerDisplay';
import HouseRunPlayerDisplay from './HouseRunPlayerDisplay';
import OneBrainPlayerDisplay from './OneBrainPlayerDisplay';
import SpeedChallengePlayerDisplay from './SpeedChallengePlayerDisplay';
import CloseOrNotPlayerDisplay from './CloseOrNotPlayerDisplay';
import DrinkingGamePlayerDisplay from './DrinkingGamePlayerDisplay';
import HotTakesPlayerDisplay from './HotTakesPlayerDisplay';
import WouldYouRatherPlayerDisplay from './WouldYouRatherPlayerDisplay';
import WhatCouldItBePlayerDisplay from './WhatCouldItBePlayerDisplay';
import { BonusLevelPlayer } from './bonus';

interface Question {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
}

interface PlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: Question;
    isBonusLevel?: boolean;
    bonusLevelPayload?: any;
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
  isBonusLevel?: boolean;
}

type PlayerDisplayComponent = React.ComponentType<PlayerDisplayProps>;

// Registry pattern for game type to component mapping
const PLAYER_DISPLAY_REGISTRY: Record<string, PlayerDisplayComponent> = {
  'collaborative-puzzle': CollaborativePlayerDisplay,
  'story-chain': CollaborativePlayerDisplay,
  'break-the-code': BreakTheCodePlayerDisplay,
  'associations': AssociationsPlayerDisplay,
  'catch-shout-catch': CatchShoutCatchPlayerDisplay,
  'house-run': HouseRunPlayerDisplay,
  'one-brain': OneBrainPlayerDisplay,
  'speed-challenge': SpeedChallengePlayerDisplay,
  'close-or-not': CloseOrNotPlayerDisplay,
  'how-many': CloseOrNotPlayerDisplay,
  'estimate-it': CloseOrNotPlayerDisplay,
  'drinking-game': DrinkingGamePlayerDisplay,
  'hot-takes': HotTakesPlayerDisplay,
  'would-you-rather': WouldYouRatherPlayerDisplay,
  'what-could-it-be': WhatCouldItBePlayerDisplay,
  'clue-in': WhatCouldItBePlayerDisplay,
  'what-am-i': WhatCouldItBePlayerDisplay,
  'guess-what': WhatCouldItBePlayerDisplay,
  // Default competitive games (trivia, word-puzzle, picture-perfect, etc.)
  'default': CompetitivePlayerDisplay,
};

/**
 * Get the appropriate player display component for a game type
 */
function getPlayerDisplayComponent(gameType: string): PlayerDisplayComponent {
  const normalizedType = normalizeGameType(gameType);
  return PLAYER_DISPLAY_REGISTRY[normalizedType] || PLAYER_DISPLAY_REGISTRY['default'];
}

// Router component - delegates to appropriate display based on game type or bonus
const PlayerDisplay: React.FC<PlayerDisplayProps> = (props) => {
  const { roomState, isBonusLevel } = props;
  const inBonus = isBonusLevel ?? roomState.isBonusLevel;
  if (inBonus) {
    return (
      <BonusLevelPlayer
        roomState={roomState}
        socket={props.socket}
        myPlayerId={props.myPlayerId}
        onGoHome={props.onGoHome}
        isHost={props.isHost}
        onBackToGameSelection={props.onBackToGameSelection}
      />
    );
  }
  const DisplayComponent = getPlayerDisplayComponent(roomState.gameType);
  return <DisplayComponent {...props} />;
};

export default PlayerDisplay;
